import React, { useRef, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Droppable, Draggable } from 'react-beautiful-dnd';
import KanbanCard from './KanbanCard';
import { Typography, IconButton, CircularProgress } from '@material-ui/core';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';

const useStyles = makeStyles(theme => ({
  column: props => ({
    backgroundColor: theme.mode === 'light' ? '#e7e7e7' : '#09090c',
    borderRadius: '16px',
    border: 'none',
    boxShadow: 'none',
    minWidth: 372,
    maxWidth: 372,
    padding: '16px',
    marginRight: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    fontFamily: '"Poppins", sans-serif',
  }),
  columnTitle: {
    fontWeight: 'bold',
    fontSize: '1.5rem',
    color: theme.mode === 'light' ? '#1a233a' : '#fff',
    fontFamily: '"Poppins", sans-serif',
    letterSpacing: '-0.025em',
  },
  cardList: {
    flexGrow: 1,
    overflowY: 'auto',
    ...theme.scrollbarStyles,
    maxHeight: 'calc(100vh - 200px)',
  },
  columnHeader: {
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  dragHandle: {
    cursor: 'grab',
    color: theme.palette.text.secondary,
    padding: theme.spacing(0.5),
    marginRight: theme.spacing(1),
    '&:hover': {
      color: theme.palette.text.primary,
    },
  },
  ticketCount: props => ({
    backgroundColor: props.color || theme.palette.primary.main,
    color: 'white',
    fontWeight: 'bold',
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1.125rem',
    boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    marginRight: '12px',
    fontFamily: '"Poppins", sans-serif',
  }),
  columnContent: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  columnTotalValue: {
    marginLeft: 'auto',
    backgroundColor: theme.mode === 'light' ? '#02b88124' : '#00ff7b17',
    color: theme.mode === 'light' ? '#02b881' : '#02b881',
    padding: '4px 8px',
    borderRadius: '8px',
    fontWeight: 600,
    fontSize: '0.875rem',
    fontFamily: '"Poppins", sans-serif',
    whiteSpace: 'nowrap',
  }
}));

const KanbanColumn = ({ id, title, tickets, color, index, updateTicket, isAdmin, pageNumber, hasMore, isLoading, onLoadMore }) => {
  const classes = useStyles({ color });
  const observerTarget = useRef(null);

  // Calcula a soma do campo "valor" dos contatos na coluna atual
  const totalValue = tickets.reduce((total, ticket) => {
    const val = ticket.valorVenda ? parseFloat(ticket.valorVenda) : 0;
    return total + (isNaN(val) ? 0 : val);
  }, 0);

  const formattedTotal = totalValue.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  // Mecanismo nativo de Scroll Infinito
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasMore && !isLoading) {
          onLoadMore(id, pageNumber + 1);
        }
      },
      { threshold: 0.1 }
    );
    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }
    return () => {
      if (observerTarget.current) observer.unobserve(observerTarget.current);
    };
  }, [id, onLoadMore, hasMore, isLoading, pageNumber]);

  return (
    <Draggable draggableId={`lane-${id}`} index={index}>
      {(provided, snapshot) => (
        <div
          className={classes.column}
          ref={provided.innerRef}
          {...provided.draggableProps}
        >
          <div className={classes.columnHeader}>
            {isAdmin && (
              <div
                {...provided.dragHandleProps}
                className={classes.dragHandle}
              >
                <DragIndicatorIcon fontSize="small" />
              </div>
            )}
            <div className={classes.ticketCount}>
              {tickets.length}
            </div>
            <Typography className={classes.columnTitle}>{title}</Typography>
            <div className={classes.columnTotalValue}>
              {formattedTotal}
            </div>
          </div>
          <Droppable droppableId={id} type="CARD">
            {(provided, snapshot) => (
              <div
                className={classes.cardList}
                ref={provided.innerRef}
                {...provided.droppableProps}
              >
                {tickets.map((ticket, index) => (
                  <KanbanCard
                    key={ticket.id}
                    ticket={ticket}
                    index={index}
                    updateTicket={updateTicket}
                  />
                ))}
                {provided.placeholder}
                {isLoading && (
                  <div style={{ display: 'flex', justifyContent: 'center', padding: '10px' }}>
                    <CircularProgress size={24} />
                  </div>
                )}
                <div ref={observerTarget} style={{ height: 10 }}></div>
              </div>
            )}
          </Droppable>
        </div>
      )}
    </Draggable>
  );
};

export default KanbanColumn;
